import type { VideoFormat } from '@hyvay/video-engine';
import apiClient from './api';

export type SocialPlatform = 'youtube' | 'tiktok' | 'linkedin' | 'facebook' | 'instagram' | 'threads';
/**
 * One OAuth flow; Meta connects Facebook Pages and their Instagram accounts together.
 * LinkedIn has two: the member's profile, and the Company Pages they administer.
 * Threads has its own login, separate from Facebook's.
 */
export type SocialProvider = 'youtube' | 'tiktok' | 'linkedin' | 'linkedin_pages' | 'meta' | 'threads';

/** Facebook/Instagram: the feed (Reel / Page video) or the Story. */
export type Placement = 'feed' | 'story';

export interface SocialConfig {
    enabled: boolean;
    providers: Record<SocialProvider, boolean>;
    verticalOnly: SocialPlatform[];
    /** Platforms that can post to a Story, and the longest video a Story takes. */
    stories: { platforms: SocialPlatform[]; maxSeconds: number };
    /** Longest caption a platform keeps (longer text is cut). */
    maxText: Partial<Record<SocialPlatform, number>>;
    maxAheadDays: number;
}

export interface SocialAccount {
    id: string;
    platform: SocialPlatform;
    kind: 'channel' | 'profile' | 'organization' | 'page' | 'ig_business' | 'threads_profile';
    name: string;
    avatarUrl: string | null;
    /**
     * EXPIRED: the token could not be refreshed, reconnect. DISCONNECTED: the user
     * disconnected it; only seen on old posts (the accounts list leaves it out).
     */
    status: 'ACTIVE' | 'EXPIRED' | 'DISCONNECTED';
    createdAt: string;
}

export interface PostOptions {
    title?: string;
    privacy?: string;
    allowComment?: boolean;
    allowDuet?: boolean;
    allowStitch?: boolean;
    brandOrganic?: boolean;
    brandedContent?: boolean;
    /** Facebook/Instagram: set per post by the server (one post per placement). */
    placement?: Placement;
    /** Sent when scheduling: which placements to create for this account. */
    placements?: Placement[];
    /** Instagram Reel: also show it in the profile feed grid (default true). */
    shareToFeed?: boolean;
    /** YouTube: audience setting required by YouTube ("made for kids"). */
    madeForKids?: boolean;
}

export type PostStatus = 'SCHEDULED' | 'PUBLISHING' | 'PUBLISHED' | 'FAILED' | 'CANCELED';

/** Engagement last read from the platform; each platform fills what it reports. */
export interface PostMetrics {
    views?: number;
    likes?: number;
    comments?: number;
    shares?: number;
    saves?: number;
    reach?: number;
}

export interface ScheduledPost {
    id: string;
    videoId: string;
    videoName: string;
    renderId: string;
    format: VideoFormat | null;
    platform: SocialPlatform;
    account: SocialAccount;
    caption: string;
    options: PostOptions | null;
    scheduledAt: string;
    status: PostStatus;
    externalUrl: string | null;
    error: string | null;
    publishedAt: string | null;
    metrics: PostMetrics | null;
    metricsAt: string | null;
    metricsError: string | null;
}

export interface TiktokCreator {
    /** false until HyVay passes TikTok's audit: privacyOptions is then only SELF_ONLY. */
    audited: boolean;
    nickname: string;
    avatarUrl?: string;
    privacyOptions: string[];
    commentDisabled: boolean;
    duetDisabled: boolean;
    stitchDisabled: boolean;
    maxDurationSec: number;
}

const data = <T>(p: Promise<{ data: { data: T } }>) => p.then((r) => r.data.data);
export const socialService = {
    config: () => data<SocialConfig>(apiClient.get('/social/config')),
    accounts: () => data<SocialAccount[]>(apiClient.get('/social/accounts')),
    /** Sends the browser to the platform's consent screen. */
    connect: async (provider: SocialProvider) => {
        const { url } = await data<{ url: string }>(apiClient.get(`/social/connect/${provider}`));
        window.location.assign(url);
    },
    finishConnect: (provider: string, code: string, state: string) =>
        data<{ connected: { platform: SocialPlatform; name: string }[] }>(apiClient.post(`/social/connect/${provider}`, { code, state })),
    /** `purge` also deletes HyVay's post history for the account (default: kept). */
    disconnect: (id: string, purge = false) => data<{ deleted: boolean; purged: boolean; canceled: number }>(apiClient.delete(`/social/accounts/${id}`, { params: purge ? { purge: '1' } : {} })),
    tiktokCreator: (accountId: string) => data<TiktokCreator>(apiClient.get(`/social/accounts/${accountId}/tiktok-creator`)),
    posts: (videoId?: string) => data<ScheduledPost[]>(apiClient.get('/social/posts', { params: videoId ? { videoId } : {} })),
    /** scheduledAt omitted = post now. `options` is keyed by account id. */
    schedule: (body: { renderId: string; accountIds: string[]; caption: string; options: Record<string, PostOptions>; scheduledAt?: string }) =>
        data<{ ids: string[]; scheduledAt: string }>(apiClient.post('/social/posts', body)),
    update: (id: string, body: { caption?: string; options?: PostOptions; scheduledAt?: string }) => data<{ id: string; scheduledAt: string }>(apiClient.patch(`/social/posts/${id}`, body)),
    cancel: (id: string) => data<{ status: string }>(apiClient.delete(`/social/posts/${id}`)),
    retry: (id: string) => data<{ status: string }>(apiClient.post(`/social/posts/${id}/retry`)),
    /** Re-reads views / likes / comments of published posts (numbers older than 10 minutes). */
    refreshInsights: () => data<{ checked: number; refreshed: number }>(apiClient.post('/social/insights/refresh')),
};

export const PLATFORM_LABEL: Record<SocialPlatform, string> = { youtube: 'YouTube', tiktok: 'TikTok', linkedin: 'LinkedIn', facebook: 'Facebook', instagram: 'Instagram', threads: 'Threads' };
export const PROVIDER_LABEL: Record<SocialProvider, string> = { youtube: 'YouTube', tiktok: 'TikTok', linkedin: 'LinkedIn (profil)', linkedin_pages: 'LinkedIn Page', meta: 'Facebook & Instagram', threads: 'Threads' };
export const POST_STATUS_LABEL: Record<PostStatus, string> = { SCHEDULED: 'Terjadwal', PUBLISHING: 'Mengirim', PUBLISHED: 'Tayang', FAILED: 'Gagal', CANCELED: 'Dibatalkan' };
export const TIKTOK_PRIVACY_LABEL: Record<string, string> = {
    PUBLIC_TO_EVERYONE: 'Semua orang',
    MUTUAL_FOLLOW_FRIENDS: 'Teman (saling follow)',
    FOLLOWER_OF_CREATOR: 'Follower',
    SELF_ONLY: 'Hanya saya',
};
